import { z } from "zod";

export const marketSchema = z.enum(["CN", "US", "KR", "CRYPTO", "GLOBAL"]);
export const categorySchema = z.enum([
  "leverage",
  "sentiment",
  "liquidity",
  "crowding",
  "volatility",
  "macro",
]);
export const directionSchema = z.enum(["up", "down", "flat"]);
export const dataStatusSchema = z.enum(["fresh", "stale", "unavailable"]);

export const sourceSchema = z.object({
  name: z.string().min(1),
  url: z.string().url().optional(),
  lastSuccess: z.string().datetime(),
  lastAttempt: z.string().datetime(),
});

export const historyPointSchema = z.object({
  timestamp: z.string().min(1),
  value: z.number().finite(),
});

export const signalSchema = z.object({
  id: z.string().min(1),
  metricId: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  market: marketSchema,
  category: categorySchema,
  frequency: z.enum(["daily", "monthly"]),
  currentValue: z.number().finite(),
  displayValue: z.string().min(1),
  unit: z.string().optional(),
  previousValue: z.number().finite().optional(),
  change1d: z.number().finite().optional(),
  change5d: z.number().finite().optional(),
  change30d: z.number().finite().optional(),
  percentile30d: z.number().min(0).max(100).optional(),
  percentile1y: z.number().min(0).max(100).optional(),
  zScore30d: z.number().finite().optional(),
  direction: directionSchema,
  strength: z.number().int().min(1).max(5),
  anomalyScore: z.number().min(0).max(100),
  persistence: z.number().int().min(0),
  timestamp: z.string().min(1),
  source: sourceSchema,
  history: z.array(historyPointSchema).min(1),
  status: dataStatusSchema,
});

export const signalSnapshotSchema = z.object({
  schemaVersion: z.literal("1.0"),
  generatedAt: z.string().datetime(),
  productionData: z.boolean(),
  signals: z.array(signalSchema),
  unavailableMetrics: z.array(
    z.object({
      metricId: z.string(),
      label: z.string(),
      market: marketSchema,
      category: categorySchema,
      reason: z.string(),
      lastAttempt: z.string().datetime(),
    }),
  ),
});

export type Market = z.infer<typeof marketSchema>;
export type Category = z.infer<typeof categorySchema>;
export type Direction = z.infer<typeof directionSchema>;
export type DataStatus = z.infer<typeof dataStatusSchema>;
export type Signal = z.infer<typeof signalSchema>;
export type SignalSnapshot = z.infer<typeof signalSnapshotSchema>;

export const MARKET_LABELS: Record<Market, string> = {
  CN: "中国",
  US: "美国",
  KR: "韩国",
  CRYPTO: "Crypto",
  GLOBAL: "全球",
};

export const CATEGORY_LABELS: Record<Category, string> = {
  leverage: "杠杆",
  sentiment: "情绪",
  liquidity: "流动性",
  crowding: "拥挤度",
  volatility: "波动率",
  macro: "宏观",
};
